import { TraitCode } from '@capital-markets-gateway/api-client-identity';
import { permissionsByEntity, useCheckAccountTraits, useCheckPermissions } from '@cmg/auth';
import type { SelectOption } from '@cmg/design-system-formik';
import defaultTo from 'lodash/defaultTo';
import uniqBy from 'lodash/uniqBy';
import { useMemo } from 'react';

import {
  Country,
  OfferingSetupExchangeMic,
  OfferingStatus,
  OfferingType,
  Region,
  Sector,
} from '../../graphql/__generated__';
import type { Common_CountryPartsFragment } from '../graphql/__generated__/Common_FilterOptions';
import { useCommon_FilterOptionsQuery } from '../graphql/__generated__/Common_FilterOptions';

type CountryOption = SelectOption<Country> & {
  region: Common_CountryPartsFragment['region'];
};

export type Props = {
  excludedOfferingTypes?: OfferingType[];
};

const EMPTY_ARRAY = [];

export const useEnumOptions = ({ excludedOfferingTypes }: Props = {}) => {
  const hasCustomSectorsPermission = useCheckPermissions([permissionsByEntity.CustomSectors.READ]);
  const hasConvertibleOfferingTypeTrait = useCheckAccountTraits([TraitCode.CONVERTS]);
  const hasInternationOfferingTypeTrait = useCheckAccountTraits([TraitCode.INTERNATIONAL]);

  const { data, loading, error } = useCommon_FilterOptionsQuery({
    variables: {
      hasCustomSectorsPermission,
    },
    fetchPolicy: 'cache-first',
  });

  const regions = useMemo(() => {
    const allRegions = defaultTo(data?.countries?.items, EMPTY_ARRAY)
      .flatMap(country => country?.region)
      .filter((region): region is NonNullable<Common_CountryPartsFragment['region']> => !!region)
      .map<SelectOption<Region>>(region => ({
        value: region.id,
        label: region.displayName,
      }));

    return uniqBy(allRegions, 'value');
  }, [data?.countries?.items]);

  const countries = useMemo(() => {
    return defaultTo(data?.countries?.items, EMPTY_ARRAY).map<CountryOption>(country => ({
      label: country.displayName,
      value: country.id,
      region: country.region,
    }));
  }, [data?.countries?.items]);

  const exchangeMics = useMemo(() => {
    return defaultTo(data?.offeringSetupExchangeMics?.items, EMPTY_ARRAY).map<
      SelectOption<OfferingSetupExchangeMic>
    >(exchangeMic => ({
      label: exchangeMic.displayName,
      value: exchangeMic.id,
    }));
  }, [data?.offeringSetupExchangeMics?.items]);

  const sectors = useMemo(() => {
    return defaultTo(data?.sectors?.items, EMPTY_ARRAY).map<SelectOption<Sector>>(sector => ({
      value: sector.id,
      label: sector.displayName,
    }));
  }, [data?.sectors?.items]);

  const customSectors = useMemo(() => {
    return defaultTo(data?.customSectors?.items, EMPTY_ARRAY).map<SelectOption<string>>(sector => ({
      value: sector.id,
      label: defaultTo(sector.name, sector.id),
    }));
  }, [data?.customSectors?.items]);

  const offeringTypes = useMemo(() => {
    return defaultTo(data?.offeringTypes?.items, EMPTY_ARRAY)
      .filter(type => !excludedOfferingTypes?.includes(type.id))
      .filter(type => {
        if (type.id === OfferingType.Abb) {
          return hasInternationOfferingTypeTrait;
        }

        if (type.id === OfferingType.Convertible) {
          return hasConvertibleOfferingTypeTrait;
        }

        return true;
      })
      .map<SelectOption<OfferingType>>(sector => ({
        value: sector.id,
        label: sector.displayName,
      }));
  }, [
    excludedOfferingTypes,
    data?.offeringTypes?.items,
    hasConvertibleOfferingTypeTrait,
    hasInternationOfferingTypeTrait,
  ]);

  const offeringStatuses = useMemo(() => {
    return defaultTo(data?.offeringStatuses?.items, EMPTY_ARRAY).map<SelectOption<OfferingStatus>>(
      sector => ({
        value: sector.id,
        label: sector.displayName,
      })
    );
  }, [data?.offeringStatuses?.items]);

  return {
    regions,
    countries,
    exchangeMics,
    sectors,
    customSectors,
    offeringTypes,
    offeringStatuses,
    error,
    loading,
  };
};
